/* eslint-disable import/order */
import React, { useEffect, useState } from 'react';
import { ProgressBar, Row, Col } from 'react-bootstrap';
import Countdown from 'react-countdown';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import BigNumber from 'bignumber.js/bignumber.js';
import { routeNames } from 'routes';
import { LaunchpadType } from 'types';
import { paddingTwoDigits } from 'helpers/utils';
import { ImEarth } from 'react-icons/im';
import { SiTelegram, SiDiscord, SiTwitter, SiYoutube, SiLinkedin, SiMedium } from 'react-icons/si';

import { useGetAccountInfo, useGetNetworkConfig, useGetIsLoggedIn, useGetPendingTransactions } from '@multiversx/sdk-dapp/hooks';
import { sendTransactions } from '@multiversx/sdk-dapp/services';
import { refreshAccount } from '@multiversx/sdk-dapp/utils';
import { ApiNetworkProvider } from '@multiversx/sdk-network-providers';
import { Account, Address, AddressValue, ContractFunction, Interaction, ResultsParser, SmartContract, SmartContractAbi, TokenPayment, TypedValue, U32Value, ArgSerializer, BigUIntValue, BytesValue, BooleanValue } from '@multiversx/sdk-core';
import axios from 'axios';
import { contracts, ElrondApiUrl, isDev, WEGLD_ID } from 'config/config';
import { BigZero, coinNames } from 'helpers/constant';
import { convertWeiToEsdt, convertEsdtToWei } from 'helpers/utils';

import UNKNOWN from 'assets/img/coins/unknown.png';
import './index.scss';

const IdoDetail = () => {
    interface Props {
        days: number;
        hours: number;
        minutes: number;
        seconds: number;
        completed: boolean;
    }

    const location = useLocation();
    const navigate = useNavigate();
    const { address, account } = useGetAccountInfo();
    const { hasPendingTransactions } = useGetPendingTransactions();
    const { network } = useGetNetworkConfig();
    const isLoggedIn = Boolean(address);
    const networkProvider = new ApiNetworkProvider(network.apiAddress);

    // load smart contract abi and parse it to SmartContract object for tx
    const [contractInteractor, setContractInteractor] = useState<any>(undefined);
    useEffect(() => {
        (async () => {
            const { address, abi } = contracts.Launchpad;

            const contractAddress = new Address(address);
            const contractAbi = new SmartContractAbi(abi);
            const contract = new SmartContract({ address: contractAddress, abi: contractAbi });

            setContractInteractor(contract);
        })();
    }, [address]); // [] makes useEffect run once

    const [project, setProject] = useState<LaunchpadType>();
    const [tokenInfo, setTokenInfo] = useState<any>();
    const [fundTokenPrice, setFundTokenPrice] = useState<number>(1);
    useEffect(() => {
        (async () => {
            if (!contractInteractor) return;

            const pathname = location.pathname;
            const project_id = pathname.substring(pathname.lastIndexOf('/') + 1);
            const args = [
                new U32Value(Number(project_id)),
            ];

            const query = contractInteractor.createQuery({
                func: new ContractFunction('getProject'),
                args: args
            });
            const resultsParser = new ResultsParser();
            const response = await networkProvider.queryContract(query);
            const endpointDefinition = contractInteractor.getEndpoint(
                'getProject'
            );
            const res = resultsParser.parseQueryResponse(
                response,
                endpointDefinition
            );
            const item = res.firstValue?.valueOf();
            const percent: any = convertWeiToEsdt(item.project_total_fund_amount, item.project_fund_token_decimal.toNumber()).multipliedBy(100).dividedBy(convertWeiToEsdt(item.project_hard_cap, item.project_fund_token_decimal.toNumber()));
            const data: any = {
                project_id: item.project_id.toNumber(),
                project_owner: item.project_owner.toString(),
                project_presale_token_identifier: item.project_presale_token_identifier.toString(),
                project_presale_token_decimal: item.project_presale_token_decimal.toNumber(),
                project_presale_token_name: item.project_presale_token_name.toString(),
                project_fund_token_identifier: item.project_fund_token_identifier.toString(),
                project_fund_token_decimal: item.project_fund_token_decimal.toNumber(),
                project_presale_token_rate: convertWeiToEsdt(item.project_presale_token_rate, item.project_presale_token_decimal.toNumber()),
                project_create_time: item.project_create_time.toNumber() * 1000,
                // project_soft_cap: convertWeiToEsdt(item.project_soft_cap, item.project_fund_token_decimal.toNumber()),
                project_hard_cap: convertWeiToEsdt(item.project_hard_cap, item.project_fund_token_decimal.toNumber()),
                project_min_buy_limit: convertWeiToEsdt(item.project_min_buy_limit, item.project_fund_token_decimal.toNumber()),
                project_max_buy_limit: convertWeiToEsdt(item.project_max_buy_limit, item.project_fund_token_decimal.toNumber()),
                project_presale_start_time: item.project_presale_start_time.toNumber() * 1000,
                project_presale_end_time: item.project_presale_end_time.toNumber() * 1000,
                project_description: item.project_description.toString(),
                project_social_website: item.project_social_website.toString(),
                project_social_telegram: item.project_social_telegram.toString(),
                project_social_discord: item.project_social_discord.toString(),
                project_social_twitter: item.project_social_twitter.toString(),
                project_social_youtube: item.project_social_youtube.toString(),
                project_social_linkedin: item.project_social_linkedin.toString(),
                project_social_medium: item.project_social_medium.toString(),
                project_is_lived: item.project_is_lived,
                project_is_verified: item.project_is_verified,
                project_total_fund_amount: convertWeiToEsdt(item.project_total_fund_amount, item.project_fund_token_decimal.toNumber()),
                project_total_bought_amount: convertWeiToEsdt(item.project_total_bought_amount, item.project_presale_token_decimal.toNumber()),
                project_buyer_count: item.project_buyer_count.toNumber(),
                project_presale_percentage: percent.toFixed(2),
                sc_owner_address: item.sc_owner_address.toString(),
            };

            if (!data.project_is_lived) {
                navigate('/lauchpad');
            }
            setProject(data);

            const res1: any = await axios.get(`${ElrondApiUrl}/tokens/${data.project_presale_token_identifier}`);
            if (res1.status == 200) {
                setTokenInfo(res1.data);
                setLogoUrl(`https://devnet-media.elrond.com/tokens/asset/${res1.data.identifier}/logo.png`);
            }

            if (data.project_fund_token_identifier == 'EGLD') {
                const res2 = await axios.get(`${ElrondApiUrl}/mex/tokens?from=0&size=10000`);
                if (res2.status == 200 && res2.data?.length > 0) {
                    const prices: any = res2.data;
                    for (let i = 0; i < prices.length; i++) {
                        if (prices[i].id === WEGLD_ID) {
                            // console.log(prices[i].price);
                            setFundTokenPrice(prices[i].price);
                            break;
                        }
                    }
                }
            }
        })();
    }, [hasPendingTransactions, contractInteractor]);

    const renderer: React.FC<Props> = ({ days, hours, minutes, seconds }) => {
        return (
            <div className='ido-custom-timer color-white'>
                <div className='ido-customer-timer-time'>{paddingTwoDigits(days)}</div>
                <div className='ido-customer-timer-time ml-2'>{paddingTwoDigits(hours)}</div>
                <div className='ido-customer-timer-time ml-2'>{paddingTwoDigits(minutes)}</div>
                <div className='ido-customer-timer-time ml-2'>{paddingTwoDigits(seconds)}</div>
            </div>
        );
    };

    const [buyAmount, setBuyAmount] = useState(0);
    const handleBuyAmount = (e: any) => {
        setBuyAmount(e.target.value);
    };

    const handleMax = () => {
        let amount = 0;
        if (project) {
            const remainedAmount = project.project_hard_cap - project.project_total_fund_amount;
            if (isLoggedIn) {
                if (convertWeiToEsdt(account.balance, 18).toNumber() > project.project_max_buy_limit) {
                    amount = project.project_max_buy_limit;
                } else {
                    amount = convertWeiToEsdt(account.balance, 18).toNumber();
                }
            } else {
                amount = project.project_max_buy_limit;
            }
            if (remainedAmount > amount) {
                setBuyAmount(amount);
            } else {
                setBuyAmount(remainedAmount);
            }
        }
    };

    const handleBuy = async () => {
        if (!project) return;
        if (!isLoggedIn) {
            toast.error('Please connect wallet to buy tokens.');
            return;
        }
        if (buyAmount <  Number(project.project_min_buy_limit) || buyAmount >  Number(project.project_max_buy_limit)) {
            toast.error('You must input the correct amount to buy.');
            return;
        }
        // const amount = (new BigNumber(buyAmount)).multipliedBy(Math.pow(10, project.project_fund_token_decimal));
        const amount = convertEsdtToWei(buyAmount, project.project_fund_token_decimal);
        if (project.project_fund_token_identifier == 'EGLD') {
            const args: TypedValue[] = [
                new U32Value(project.project_id),
            ];
            const { argumentsString } = new ArgSerializer().valuesToString(args);
            const data = `buy@${argumentsString}`;

            const tx = {
                receiver: contracts.Launchpad.address,
                gasLimit: 10000000,
                value: amount,
                data: data,
            };

            await refreshAccount();
            const result = await sendTransactions({
                transactions: tx,
            });
        } else {
            const args: TypedValue[] = [
                BytesValue.fromUTF8(project.project_fund_token_identifier),
                new BigUIntValue(amount.valueOf()),
                BytesValue.fromUTF8('buy'),
                new U32Value(project.project_id),
            ];
            const { argumentsString } = new ArgSerializer().valuesToString(args);
            const data = `ESDTTransfer@${argumentsString}`;

            const tx = {
                receiver: contracts.Launchpad.address,
                gasLimit: 10000000,
                value: 0,
                data: data,
            };

            await refreshAccount();
            const result = await sendTransactions({
                transactions: tx,
            });
        }
    };

    const handleDelete = async () => {
        if (!project) return;
        const args: TypedValue[] = [
            new U32Value(project.project_id),
            new BooleanValue(false),
        ];
        const { argumentsString } = new ArgSerializer().valuesToString(args);
        const data = `setProjectStatus@${argumentsString}`;

        const tx = {
            receiver: contracts.Launchpad.address,
            gasLimit: 10000000,
            value: 0,
            data: data,
        };

        await refreshAccount();
        await sendTransactions({
            transactions: tx,
        });
    };

    const handleWithdraw = async () => {
        if (!project) return;
        const args: TypedValue[] = [
            new U32Value(project.project_id),
        ];
        const { argumentsString } = new ArgSerializer().valuesToString(args);
        const data = `withdrawProject@${argumentsString}`;

        const tx = {
            receiver: contracts.Launchpad.address,
            gasLimit: 10000000,
            value: 0,
            data: data,
        };

        await refreshAccount();
        await sendTransactions({
            transactions: tx,
        });
    };

    const [logoUrl, setLogoUrl] = useState<string>(UNKNOWN);
    const onErrorLogo = () => {
        setLogoUrl(UNKNOWN);
    };

    return (
        <>
            <div className='launchpad-detail-container mt-5 mb-5'>
                <Row>
                    <Col lg={12}>
                        <Link to={routeNames.launchpad}>
                            <p className='go-back'> {'< Back to Launch'}</p>
                        </Link>
                    </Col>
                </Row>

                <Row className='home-container'>
                    <Col lg={6}>
                        <div className='ido-detail-box'>
                            <div className='d-flex align-items-center'>
                                <div className='d-flex'>
                                    <div>
                                        <img src={logoUrl} alt={project?.project_presale_token_identifier} width={'40px'} onError={onErrorLogo} />
                                    </div>
                                </div>
                                <div className='d-flex flex-column ml-4'>
                                    <span className='ido-detail-title'>{project ? project.project_presale_token_identifier : '-'}</span>
                                    <span className='ido-detail-token-id mt-2'>{`${tokenInfo ? tokenInfo.name : '-'} / ${project && project.project_fund_token_identifier == 'EGLD' ? 'EGLD' : 'USDC'}`}</span>
                                </div>
                            </div>

                            <div className='mt-4'>
                                {/* <div className='connect-but-box d-flex justify-content-center'>
                                    <button className='ido-detail-buttton'> Connect Wallet </button>
                                </div> */}

                                <div className='d-flex flex-column mt-3'>
                                    <span className='ido-detail-rate'>{`1 ${project && project.project_fund_token_identifier == 'EGLD' ? 'EGLD' : 'USDC'} = ${project ? project.project_presale_token_rate.toString() : 'undefined'} ${tokenInfo ? tokenInfo.name : '-'}`}</span>
                                    <span className='ido-detail-rate' style={{ fontSize: '13px' }}>{`1 ${tokenInfo ? tokenInfo.name : '-'} = $${project && (fundTokenPrice / project.project_presale_token_rate).toFixed(5)}`}</span>
                                </div>

                                <div className='mt-3'>
                                    <span>{'Progress (' + project?.project_presale_percentage + '%)'} - {project ? project.project_total_fund_amount.toString() : '-'} {project && project.project_fund_token_identifier == 'EGLD' ? 'EGLD' : 'USDC'}</span>
                                    <ProgressBar className='mt-1' now={project?.project_presale_percentage} />
                                    <div className='d-flex justify-content-between mt-1'>
                                        <span>{`0 ${project && project.project_fund_token_identifier == 'EGLD' ? 'EGLD' : 'USDC'}`}</span>
                                        <span>{`${project ? project.project_hard_cap.toString() : '-'} ${project && project.project_fund_token_identifier == 'EGLD' ? 'EGLD' : 'USDC'}`}</span>
                                    </div>
                                </div>
                            </div>
                            {project && Number(project.project_total_fund_amount) < Number(project.project_hard_cap) ? (
                                <>
                                    <div className='mt-4 text-center'>
                                        <span className='ido-detail-rate'>{project && project?.project_presale_start_time > Date.now() ? 'STARTS IN' : (project && project?.project_presale_end_time > Date.now() ? 'END IN' : 'ENDED')}</span>
                                    </div>
                                    {project && (
                                        <div className='d-flex justify-content-center mt-2'>
                                            <Countdown className='ido-detail-countdown' date={project?.project_presale_start_time > Date.now() ? project?.project_presale_start_time : project?.project_presale_end_time} renderer={renderer} autoStart />
                                        </div>
                                    )}

                                    <div className='mt-4 d-flex'>
                                        <input className='ido-input' value={buyAmount} onChange={handleBuyAmount} />
                                        <button className='ido-detail-buttton ml-2' style={{ padding: '5px 10px' }} onClick={handleMax}> MAX </button>
                                    </div>
                                </>
                            ) : (
                                <div className='mt-4 text-center'>
                                    <span className='ido-detail-rate'>SOLD OUT</span>
                                </div>
                            )}

                            {
                                project && Number(project.project_total_fund_amount) < Number(project.project_hard_cap) && project.project_presale_end_time > Date.now() ? (
                                    <div className='d-flex justify-content-center mt-4'>
                                        <button className='ido-detail-buy-button' onClick={handleBuy}> Buy </button>
                                    </div>
                                ) : (
                                    <></>
                                )
                            }

                        </div>
                        <div className='ido-detail-box mt-4 mb-4' style={{ border: 'none', fontSize: '14px', padding: '30px' }}>
                            {/* <div className='d-flex justify-content-between'>
                                <span>Registration Start: </span>
                                <span style={{ color: 'white' }}>{`${currentPoolDetail.registration_start}`}</span>
                            </div>
                            <div className='d-flex justify-content-between mt-2'>
                                <span>Registration Start: </span>
                                <span style={{ color: 'white' }}>{`${currentPoolDetail.registration_end}`}</span>
                            </div> */}
                            <div className='d-flex justify-content-between mt-2'>
                                <span>Minimum Buy: </span>
                                <span style={{ color: 'white' }}>{`${project ? project.project_min_buy_limit.toString() : 'NAN'} ${project && project.project_fund_token_identifier == 'EGLD' ? 'EGLD' : 'USDC'}`}</span>
                            </div>
                            <div className='d-flex justify-content-between mt-2'>
                                <span>Maximum Buy: </span>
                                <span style={{ color: 'white' }}>{`${project ? project.project_max_buy_limit.toString() : 'NAN'} ${project && project.project_fund_token_identifier == 'EGLD' ? 'EGLD' : 'USDC'}`}</span>
                            </div>
                        </div>
                        {project && project.sc_owner_address == address ? (
                            <div className='ido-detail-box mt-4 mb-4' style={{ border: 'none', fontSize: '14px', padding: '30px' }}>
                                <div className='d-flex justify-content-center mt-2'>
                                    <button className='ido-detail-buttton ml-2' style={{ padding: '5px 10px' }} onClick={handleDelete}>Delete project</button>
                                </div>
                                <div className='d-flex justify-content-center mt-2'>
                                    <button className='ido-detail-buttton ml-2' style={{ padding: '5px 10px' }} onClick={handleWithdraw}>Withdraw project</button>
                                </div>
                            </div>
                        ) : (
                            <></>
                        )}
                    </Col>

                    <Col lg={6}>
                        <div className='ido-detail-box'>
                            <div className='d-flex align-items-center'>
                                <div className='d-flex'>
                                    <div>
                                        <img src={logoUrl} alt={project?.project_presale_token_identifier} width={'40px'} onError={onErrorLogo} />
                                    </div>
                                </div>
                                <div className='d-flex flex-column ml-4'>
                                    <span className='ido-detail-title' style={{ fontSize: '20px' }}>{project ? project.project_presale_token_identifier : '-'}</span>
                                </div>
                            </div>

                            <div className='mt-3'>
                                <div style={{ fontSize: '16px', color: 'white' }}>{project ? project.project_description : ''}</div>
                                {/* <div className='mt-2' style={{ fontSize: '16px', color: '#E3E3E3' }}>{'Your investment is protected, this sale is under the Safeguarded Launch Protocol rules.'}</div> */}
                            </div>

                            <div className='social-box mt-4'>
                                {
                                    project?.project_social_website !== '' && (
                                        <a className='social-link' href={project?.project_social_website} rel='noreferrer' target='_blank'>
                                            <ImEarth />
                                        </a>
                                    )
                                }
                                {
                                    project?.project_social_telegram !== '' && (
                                        <a className='social-link' href={project?.project_social_telegram} rel='noreferrer' target='_blank'>
                                            <SiTelegram />
                                        </a>
                                    )
                                }
                                {
                                    project?.project_social_discord !== '' && (
                                        <a className='social-link' href={project?.project_social_discord} rel='noreferrer' target='_blank'>
                                            <SiDiscord />
                                        </a>
                                    )
                                }
                                {
                                    project?.project_social_twitter !== '' && (
                                        <a className='social-link' href={project?.project_social_twitter} rel='noreferrer' target='_blank'>
                                            <SiTwitter />
                                        </a>
                                    )
                                }
                                {
                                    project?.project_social_youtube !== '' && (
                                        <a className='social-link' href={project?.project_social_youtube} rel='noreferrer' target='_blank'>
                                            <SiYoutube />
                                        </a>
                                    )
                                }
                                {
                                    project?.project_social_linkedin !== '' && (
                                        <a className='social-link' href={project?.project_social_linkedin} rel='noreferrer' target='_blank'>
                                            <SiLinkedin />
                                        </a>
                                    )
                                }
                                {
                                    project?.project_social_medium !== '' && (
                                        <a className='social-link' href={project?.project_social_medium} rel='noreferrer' target='_blank'>
                                            <SiMedium />
                                        </a>
                                    )
                                }
                            </div>

                            <div className='mt-5'>
                                <div className='d-flex flex-column' style={{ rowGap: '6px' }}>
                                    <p style={{ fontSize: '18px', color: '#FFBC0A', fontWeight: '700' }}>TOKEN</p>
                                    <div>
                                        <span>Token: </span>
                                        <span style={{ color: '#fff' }}>{tokenInfo ? tokenInfo.name : '-'}</span>
                                    </div>
                                    <div>
                                        <span>Token Identifier: </span>
                                        <span style={{ color: '#fff' }}>{project?.project_presale_token_identifier}</span>
                                    </div>
                                    <div>
                                        <span>Token Decimal: </span>
                                        <span style={{ color: '#fff' }}>{tokenInfo ? tokenInfo.decimals : 'undefiend'}</span>
                                    </div>
                                    <div>
                                        <span>Total Supply: </span>
                                        <span style={{ color: '#fff' }}>{`${tokenInfo?.supply.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} ${tokenInfo ? tokenInfo.name : '-'}`}</span>
                                    </div>
                                </div>

                                <div className='d-flex flex-column mt-5' style={{ rowGap: '6px' }}>
                                    <p style={{ fontSize: '18px', color: '#FFBC0A', fontWeight: '700' }}>PRICE</p>
                                    <div>
                                        <span>1 ${tokenInfo ? tokenInfo.name : '-'} : </span>
                                        <span style={{ color: '#fff' }}>${project && (fundTokenPrice / project?.project_presale_token_rate).toFixed(5)}</span>
                                    </div>
                                </div>

                                <div className='d-flex flex-column mt-5' style={{ rowGap: '6px' }}>
                                    <p style={{ fontSize: '18px', color: '#FFBC0A', fontWeight: '700' }}>POOL DETAILS</p>
                                    <div>
                                        <span>Sale Type: </span>
                                        {/* <span style={{ color: '#fff' }}>{saleType === 'sale_type_0' ? 'Public' : (saleType === 'sale_type_1' ? 'Private' : 'Whitelist')}</span> */}
                                        <span style={{ color: '#fff' }}>Public</span>
                                    </div>
                                    <div>
                                        <span>Starts / end: </span>
                                        <span style={{ color: '#fff' }}>{`${moment(project?.project_presale_start_time).format('DD MMM YYYY hh:mm a')} - ${moment(project?.project_presale_end_time).format('DD MMM YYYY hh:mm a')}`}</span>
                                    </div>
                                    {/* <div>
                                        <span>Registration: </span>
                                        <span style={{ color: '#fff' }}>{`${currentPoolDetail.registration_start} - ${currentPoolDetail.registration_end}`}</span>
                                    </div> */}
                                </div>

                                {/* <div className='d-flex flex-column mt-5' style={{ rowGap: '6px' }}>
                                    <p style={{ fontSize: '20px', color: '#fff', fontWeight: '700' }}>DISTRIBUTION</p>
                                    <div>
                                        <span>Distribution: </span>
                                        <span style={{ color: '#fff' }}>Claimed on Launchpad</span>
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default IdoDetail;